import { useMemo } from "react";
import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BaseChartProps, ChartWrapper } from "./chart-utils";

import {
  chartColors,
  formatNumber,
  defaultResponsiveConfig,
  generateRandomData,
} from "./chart-helpers";

import {
  VerticalAlignmentType,
  HorizontalAlignmentType,
} from "recharts/types/component/DefaultLegendContent";

interface AreaChartProps extends BaseChartProps {
  strokeWidth?: number;
  curved?: boolean;
  fillOpacity?: number;
  showDots?: boolean;
  stacked?: boolean;
}

export default function AreaChart({
  data,
  series,
  title,
  subtitle,
  height = 300,
  width = "100%",
  className,
  colors = chartColors.series,
  showLegend = true,
  showTooltip = true,
  showGrid = true,
  animate = true,
  legendPosition = "bottom",
  strokeWidth = 2,
  curved = true,
  showDots = false,
  fillOpacity = 0.6,
  stacked = false,
}: AreaChartProps) {
  const chartData = useMemo(() => {
    if (series && series.length > 0) {
      const categories = [
        ...new Set(series.flatMap((s) => s.data.map((d) => d.name))),
      ];
      return categories.map((category) => {
        const item: Record<string, string | number> = { name: category };
        series.forEach((s) => {
          const dataPoint = s.data.find((d) => d.name === category);
          item[s.name] = dataPoint ? dataPoint.value : 0;
        });
        return item;
      });
    }

    return data || generateRandomData(8, 100);
  }, [data, series]);

  const legendProps = useMemo(() => {
    const baseProps = {
      wrapperStyle: {
        paddingTop: legendPosition === "bottom" ? 20 : 0,
      },
    };

    switch (legendPosition) {
      case "top":
        return { ...baseProps, verticalAlign: "top" as VerticalAlignmentType };
      case "right":
        return {
          ...baseProps,
          verticalAlign: "middle" as VerticalAlignmentType,
          align: "right" as HorizontalAlignmentType,
        };
      case "left":
        return {
          ...baseProps,
          verticalAlign: "middle" as VerticalAlignmentType,
          align: "left" as HorizontalAlignmentType,
        };
      default:
        return {
          ...baseProps,
          verticalAlign: "bottom" as VerticalAlignmentType,
        };
    }
  }, [legendPosition]);

  return (
    <ChartWrapper
      title={title}
      subtitle={subtitle}
      className={className}
      height={height}
      width={width}
    >
      <ResponsiveContainer {...defaultResponsiveConfig}>
        <RechartsAreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
        >
          {showGrid && (
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
          )}
          <XAxis
            dataKey="name"
            stroke={chartColors.foreground}
            tick={{ fill: chartColors.foreground }}
          />
          <YAxis
            tickFormatter={formatNumber}
            stroke={chartColors.foreground}
            tick={{ fill: chartColors.foreground }}
          />
          {showTooltip && <Tooltip />}
          {showLegend && <Legend {...legendProps} />}

          {series && series.length > 0 ? (
            series.map((s, idx) => (
              <Area
                key={s.name}
                type={curved ? "monotone" : "linear"}
                dataKey={s.name}
                stroke={colors[idx % colors.length]}
                strokeWidth={strokeWidth}
                fill={colors[idx % colors.length]}
                fillOpacity={fillOpacity}
                dot={showDots}
                activeDot={{ r: 8 }}
                isAnimationActive={animate}
                stackId={stacked ? "stack" : undefined}
              />
            ))
          ) : (
            <Area
              type={curved ? "monotone" : "linear"}
              dataKey="value"
              stroke={colors[0]}
              strokeWidth={strokeWidth}
              fill={colors[0]}
              fillOpacity={fillOpacity}
              dot={showDots}
              activeDot={{ r: 8 }}
              isAnimationActive={animate}
            />
          )}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}
